import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useResearchCapability } from "@/hooks/useResearchCapability";

// AUDIENCE-SEPARATION (Phase 4 build-wall): this module is PROPRIETARY — it names the
// `has_surface_access` RPC, so it must live only in the INTERNAL import graph. It is imported
// only by internal-graph modules (SurfaceGate, ResearchHubLayout, useTabVisibility). The neutral
// `usePublishedInsightAccess` gate that external advisers need was moved to its own file
// (@/hooks/usePublishedInsightAccess) and is re-exported here so existing internal import sites
// keep working; the adviser/public entries import it from the neutral module and never touch
// this file.
export { usePublishedInsightAccess } from "@/hooks/usePublishedInsightAccess";
export type { PublishedInsightAccess } from "@/hooks/usePublishedInsightAccess";

// Surface-aware gating for the Research Hub reader shell.
//
// The app's coarse `requiredTeam="Investment"` gate cannot express
// "Bongiwe = analyst only". `has_surface_access('cio'|'pm'|'analyst')` can — and the
// backend already encodes the admin bypass (admins/superadmins return true for every
// surface). We mirror `useResearchCapability` so each surface caches independently and
// we never touch the shared `useAuth` hook.

export type Surface = "cio" | "pm" | "analyst";

export function useSurfaceAccess(surface: Surface | undefined) {
  return useQuery({
    queryKey: ["has_surface_access", surface],
    enabled: !!surface,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("has_surface_access", { _surface: surface as Surface });
      if (error) throw error;
      return Boolean(data);
    },
  });
}

export interface ResearchHubAccess {
  loading: boolean;
  canRead: boolean;
  seeCio: boolean;
  seePm: boolean;
  seeAnalyst: boolean;
  isAdmin: boolean;
}

// One call the shell uses to decide which tabs to render and where to land.
export function useResearchHubAccess(): ResearchHubAccess {
  const { staffMember } = useAuth();
  const isAdmin = staffMember?.role_level === "admin" || staffMember?.role_level === "superadmin";
  const read = useResearchCapability("read");
  const cio = useSurfaceAccess("cio");
  const pm = useSurfaceAccess("pm");
  const analyst = useSurfaceAccess("analyst");

  return {
    loading: read.isLoading || cio.isLoading || pm.isLoading || analyst.isLoading,
    canRead: Boolean(read.data),
    seeCio: Boolean(cio.data),
    seePm: Boolean(pm.data),
    seeAnalyst: Boolean(analyst.data),
    isAdmin,
  };
}

